import { BloodworkAnalysis } from '../models/BloodworkAnalysis';

export interface WBCMark {
  name: string;
  direction: 'up' | 'down';
}

export interface WBCPattern {
  title: string;
  description: string[];
  markers: WBCMark[];
}

export interface WBCEvaluationData {
  immuneActivityStatus?: WBCPattern;
  immuneResponseBalance?: WBCPattern;
  acuteInnateImmuneResponse?: WBCPattern;
  acuteAdaptiveImmuneResponse?: WBCPattern;
  chronicInnateImmuneResponse?: WBCPattern;
  chronicAdaptiveImmuneResponse?: WBCPattern;
  parasiticActivity?: WBCPattern;
  monocyteEvaluation?: WBCPattern;
  basophilEvaluation?: WBCPattern;
  eosinophilsAlone?: WBCPattern;
  basoMonoPattern?: WBCPattern;
}

export class WBCEvaluationHelper {
  static evaluate(analysis: BloodworkAnalysis): WBCEvaluationData {
    const data: WBCEvaluationData = {};

    if (!analysis.bloodwork) return data;
    const wbc = analysis.wbc;

    // Hide everything if WBC has no value
    if (!wbc || !wbc.hasValue || !wbc.hasValue()) return data;

    const acuteInnate = this.getAcuteInnateImmuneResponse(analysis);
    if (acuteInnate) data.acuteInnateImmuneResponse = acuteInnate;

    const acuteAdaptive = this.getAcuteAdaptiveImmuneResponse(analysis);
    if (acuteAdaptive) data.acuteAdaptiveImmuneResponse = acuteAdaptive;

    const chronicInnate = this.getChronicInnateImmuneResponse(analysis);
    if (chronicInnate) data.chronicInnateImmuneResponse = chronicInnate;

    const chronicAdaptive = this.getChronicAdaptiveImmuneResponse(analysis);
    if (chronicAdaptive) data.chronicAdaptiveImmuneResponse = chronicAdaptive;

    const showNoMajorPatterns =
      !acuteInnate && !acuteAdaptive && !chronicInnate && !chronicAdaptive;

    if (showNoMajorPatterns) {
      const activity = this.getImmuneActivityStatus(analysis);
      if (activity) data.immuneActivityStatus = activity;

      const balance = this.getImmuneResponseBalance(analysis);
      if (balance) data.immuneResponseBalance = balance;
    }

    const parasitic = this.getParasiticActivity(analysis);
    if (parasitic) data.parasiticActivity = parasitic;

    const mono = this.getMonocyteEvaluation(analysis);
    if (mono) data.monocyteEvaluation = mono;

    const baso = this.getBasophilEvaluation(analysis);
    if (baso) data.basophilEvaluation = baso;

    const eos = this.getEosinophilsAlone(analysis);
    if (eos) data.eosinophilsAlone = eos;

    const basoMono = this.getBasoMonoPattern(analysis);
    if (basoMono) data.basoMonoPattern = basoMono;

    return data;
  }

  // Pattern 1 & 2
  private static getImmuneActivityStatus(
    analysis: BloodworkAnalysis,
  ): WBCPattern | undefined {
    const wbc = analysis?.wbc;
    if (!wbc?.hasValue?.()) return undefined;

    if (wbc.isGreaterThanLab?.() || wbc.isGreaterThanOptimal?.()) {
      return {
        title: 'Increased Immune Activity',
        description: [],
        markers: [{ name: 'WBC', direction: 'up' }],
      };
    } else if (wbc.isLessThanLab?.() || wbc.isLessThanOptimal?.()) {
      return {
        title: 'Reduced Immune Activity',
        description: [],
        markers: [{ name: 'WBC', direction: 'down' }],
      };
    }
    return undefined;
  }

  // Pattern 3 & 4
  private static getImmuneResponseBalance(
    analysis: BloodworkAnalysis,
  ): WBCPattern | undefined {
    const nlr = analysis?.nlr;
    if (!nlr?.hasValue?.()) return undefined;

    if (nlr.isGreaterThanLab?.() || nlr.isGreaterThanOptimal?.()) {
      return {
        title: 'Innate Immune Dominance',
        description: [
          'Bacterial Activity',
          'Acute Inflammatory Activity',
          'Acute Stress',
        ],
        markers: [{ name: 'NLR', direction: 'up' }],
      };
    }

    if (nlr.isLessThanLab?.() || nlr.isLessThanOptimal?.()) {
      return {
        title: 'Adaptive Immune Dominance',
        description: ['Viral Activity', 'Autoimmunity', 'Chronic Stress'],
        markers: [{ name: 'NLR', direction: 'down' }],
      };
    }
    return undefined;
  }

  // Pattern 5
  private static getAcuteInnateImmuneResponse(
    analysis: BloodworkAnalysis,
  ): WBCPattern | undefined {
    const wbc = analysis?.wbc;
    const nlr = analysis?.nlr;

    const wbcHigh = wbc?.isGreaterThanLab?.() || wbc?.isGreaterThanOptimal?.();
    const nlrHigh = nlr?.isGreaterThanLab?.() || nlr?.isGreaterThanOptimal?.();

    if (wbcHigh && nlrHigh) {
      return {
        title: 'Increased Immune Activity with Innate Immune Dominance',
        description: [
          'Bacterial Activity',
          'Acute Inflammatory Activity',
          'Acute Stress',
        ],
        markers: [
          { name: 'WBC', direction: 'up' },
          { name: 'NLR', direction: 'up' },
        ],
      };
    }
    return undefined;
  }

  // Pattern 6
  private static getAcuteAdaptiveImmuneResponse(
    analysis: BloodworkAnalysis,
  ): WBCPattern | undefined {
    const wbc = analysis?.wbc;
    const nlr = analysis?.nlr;

    if (
      (wbc?.isGreaterThanLab?.() || wbc?.isGreaterThanOptimal?.()) &&
      (nlr?.isLessThanLab?.() || nlr?.isLessThanOptimal?.())
    ) {
      return {
        title: 'Increased Immune Activity with Adaptive Immune Dominance',
        description: ['Viral Activity', 'Autoimmunity', 'Chronic Stress'],
        markers: [
          { name: 'WBC', direction: 'up' },
          { name: 'NLR', direction: 'down' },
        ],
      };
    }
    return undefined;
  }

  // Pattern 7
  private static getChronicInnateImmuneResponse(
    analysis: BloodworkAnalysis,
  ): WBCPattern | undefined {
    const wbc = analysis?.wbc;
    const nlr = analysis?.nlr;

    const wbcLow = wbc?.isLessThanLab?.() || wbc?.isLessThanOptimal?.();
    const nlrHigh = nlr?.isGreaterThanLab?.() || nlr?.isGreaterThanOptimal?.();

    if (wbcLow && nlrHigh) {
      return {
        title: 'Reduced Immune Activity with Innate Immune Dominance',
        description: [
          'Ongoing Bacterial Activity',
          'Chronic Inflammatory Activity',
          'Unresolved Acute Stress',
        ],
        markers: [
          { name: 'WBC', direction: 'down' },
          { name: 'NLR', direction: 'up' },
        ],
      };
    }
    return undefined;
  }

  // Pattern 8
  private static getChronicAdaptiveImmuneResponse(
    analysis: BloodworkAnalysis,
  ): WBCPattern | undefined {
    const wbc = analysis?.wbc;
    const nlr = analysis?.nlr;

    const wbcLow = wbc?.isLessThanLab?.() || wbc?.isLessThanOptimal?.();
    const nlrLow = nlr?.isLessThanLab?.() || nlr?.isLessThanOptimal?.();

    if (wbcLow && nlrLow) {
      return {
        title: 'Reduced Immune Activity with Adaptive Immune Dominance',
        description: ['Viral Activity', 'Autoimmunity', 'Chronic Stress'],
        markers: [
          { name: 'WBC', direction: 'down' },
          { name: 'NLR', direction: 'down' },
        ],
      };
    }
    return undefined;
  }

  private static isEosinophilsHigh(analysis: BloodworkAnalysis): boolean {
    const eosAbs = analysis?.eosinophilsAbs;
    const eosPct = analysis?.eosinophils;
    return !!(
      eosAbs?.isGreaterThanOptimal?.() ||
      eosAbs?.isGreaterThanLab?.() ||
      eosPct?.isGreaterThanOptimal?.() ||
      eosPct?.isGreaterThanLab?.()
    );
  }

  private static isMonocytesHigh(analysis: BloodworkAnalysis): boolean {
    const monoAbs = analysis?.monocytesAbs;
    const monoPct = analysis?.monocytes;
    return !!(
      monoAbs?.isGreaterThanOptimal?.() ||
      monoAbs?.isGreaterThanLab?.() ||
      monoPct?.isGreaterThanOptimal?.() ||
      monoPct?.isGreaterThanLab?.()
    );
  }

  private static isBasophilsHigh(analysis: BloodworkAnalysis): boolean {
    const basoAbs = analysis?.basophilsAbs;
    const basoPct = analysis?.basophils;
    return !!(
      basoAbs?.isGreaterThanOptimal?.() ||
      basoAbs?.isGreaterThanLab?.() ||
      basoPct?.isGreaterThanOptimal?.() ||
      basoPct?.isGreaterThanLab?.()
    );
  }

  private static shouldShowImmuneCleanupRepair(
    analysis: BloodworkAnalysis,
  ): boolean {
    const monoHigh = this.isMonocytesHigh(analysis);
    const basoHigh = this.isBasophilsHigh(analysis);
    return monoHigh && !basoHigh;
  }

  // Pattern 13
  private static getEosinophilsAlone(
    analysis: BloodworkAnalysis,
  ): WBCPattern | undefined {
    const eosHigh = this.isEosinophilsHigh(analysis);
    const monoHigh = this.isMonocytesHigh(analysis);
    const basoHigh = this.isBasophilsHigh(analysis);

    if (eosHigh && !monoHigh && !basoHigh) {
      return {
        title:
          'Possible Sensitivity Response (IgG) to Food or Environmental Antigen',
        description: [],
        markers: [{ name: 'Eos', direction: 'up' }],
      };
    }
    return undefined;
  }

  // Pattern 14
  private static getBasoMonoPattern(
    analysis: BloodworkAnalysis,
  ): WBCPattern | undefined {
    const monoHigh = this.isMonocytesHigh(analysis);
    const basoHigh = this.isBasophilsHigh(analysis);

    if (monoHigh && basoHigh) {
      return {
        title: 'Immune Cleanup and Repair with Possible Histamine Release',
        description: [],
        markers: [
          { name: 'Mono', direction: 'up' },
          { name: 'Baso', direction: 'up' },
        ],
      };
    }
    return undefined;
  }

  // Pattern 10 & 11 (Parasitic Activity)
  private static getParasiticActivity(
    analysis: BloodworkAnalysis,
  ): WBCPattern | undefined {
    const eosHigh = this.isEosinophilsHigh(analysis);
    const monoHigh = this.isMonocytesHigh(analysis);
    const basoHigh = this.isBasophilsHigh(analysis);

    // Pattern 10
    if (eosHigh && monoHigh) {
      const markers: WBCMark[] = [
        { name: 'Eos', direction: 'up' },
        { name: 'Mono', direction: 'up' },
      ];
      if (basoHigh) {
        markers.push({ name: 'Baso', direction: 'up' });
      }

      return {
        title:
          'Possible Immune Reactivity Response to Chronic Antigen Exposure:',
        description: [
          'Food Sensitivities',
          'Environmental Toxins',
          'Parasitic Activity',
        ],
        markers,
      };
    }

    // Pattern 11
    if (eosHigh && basoHigh && !monoHigh) {
      return {
        title:
          'Possible Allergic Response (IgE) to Food or Environmental Antigen',
        description: [],
        markers: [
          { name: 'Eos', direction: 'up' },
          { name: 'Baso', direction: 'up' },
        ],
      };
    }

    return undefined;
  }

  // Pattern 12
  private static getMonocyteEvaluation(
    analysis: BloodworkAnalysis,
  ): WBCPattern | undefined {
    if (!this.shouldShowImmuneCleanupRepair(analysis)) return undefined;

    return {
      title: 'Immune Cleanup and Repair',
      description: [],
      markers: [{ name: 'Mono', direction: 'up' }],
    };
  }

  // Pattern 15
  private static getBasophilEvaluation(
    analysis: BloodworkAnalysis,
  ): WBCPattern | undefined {
    const eosHigh = this.isEosinophilsHigh(analysis);
    const monoHigh = this.isMonocytesHigh(analysis);
    const basoHigh = this.isBasophilsHigh(analysis);

    // Suppress if parasitic activity pattern is present (all three high)
    if (eosHigh && monoHigh && basoHigh) return undefined;

    // Suppress if eos + baso (allergic response pattern)
    if (eosHigh && basoHigh && !monoHigh) return undefined;

    // Suppress if mono + baso (baso-mono pattern)
    if (!eosHigh && monoHigh && basoHigh) return undefined;

    if (!basoHigh) return undefined;

    return {
      title: 'Possible Inflammation / Immune Activation',
      description: [],
      markers: [{ name: 'Baso', direction: 'up' }],
    };
  }
}
